import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    FiBriefcase, FiPlus, FiX, FiCheck, 
    FiClock, FiSearch, FiEdit2, FiTrash2, FiUserCheck, 
    FiUsers, FiAlertTriangle, FiFilter, FiArchive, FiArrowRight,
    FiTrendingUp, FiCheckCircle, FiList, FiGrid, FiMoreHorizontal
} from 'react-icons/fi';
import { FaUserTie } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './GestaoVagas.css'; 

// --- Componentes Auxiliares (Badges e Skeletons) ---

const SkeletonRow = () => (
    <div className="skeleton-row">
        <div className="skeleton-block" style={{width: '40px'}}></div>
        <div className="skeleton-block" style={{width: '200px'}}></div>
        <div className="skeleton-block" style={{width: '100px'}}></div>
        <div className="skeleton-block" style={{width: '80px'}}></div>
        <div className="skeleton-block" style={{width: '100px'}}></div>
        <div className="skeleton-block" style={{width: '80px'}}></div>
        <div className="skeleton-block" style={{width: '50px'}}></div>
    </div>
);

const StatusBadge = ({ status }) => {
    const map = {
        'aberta': { class: 'badge-blue', label: 'Aberta' },
        'em_andamento': { class: 'badge-yellow', label: 'Em Andamento' },
        'preenchida': { class: 'badge-green', label: 'Preenchida' },
        'cancelada': { class: 'badge-red', label: 'Cancelada' },
        'pendente_aprovacao': { class: 'badge-gray', label: 'Em Aprovação' }
    };
    const info = map[status] || { class: 'badge-gray', label: status };
    return <span className={`status-pill ${info.class}`}>{info.label}</span>;
};

const UrgencyBadge = ({ level }) => {
    const map = {
        'baixa': { color: 'text-green', bg: 'bg-green-light', text: 'Baixa' },
        'normal': { color: 'text-blue', bg: 'bg-blue-light', text: 'Normal' },
        'alta': { color: 'text-orange', bg: 'bg-orange-light', text: 'Alta' },
        'critica': { color: 'text-red', bg: 'bg-red-light', text: 'Crítica' }
    };
    const info = map[level] || map['normal'];
    return (
        <span className={`urgency-tag ${info.color} ${info.bg}`}>
            {info.text}
        </span>
    );
};

// --- MODAIS ---

const ModalConclusao = ({ onClose, vaga, onConcluir }) => {
    const [novoId, setNovoId] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!novoId) return toast.warn("Informe o ID do novo colaborador.");
        
        setLoading(true);
        try {
            await onConcluir(vaga.id, parseInt(novoId));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="overlay-backdrop">
            <div className="modal-premium">
                <div className="modal-header">
                    <h3><FiCheckCircle className="icon-header success" /> Concluir Vaga</h3>
                    <button onClick={onClose} className="btn-close"><FiX /></button>
                </div>
                <form onSubmit={handleSubmit} className="modal-body">
                    <div className="alert-box info">
                        <FiUserCheck />
                        <div>
                            <strong>Finalizando: {vaga.titulo}</strong>
                            <p>Ao concluir, a vaga será arquivada e o processo de admissão iniciado.</p>
                        </div>
                    </div>
                    <div className="form-group" style={{ marginTop: '1.5rem' }}>
                        <label className="input-label">ID do Novo Colaborador (Pré-cadastro)</label>
                        <input 
                            type="number" 
                            className="input-modern" 
                            placeholder="Ex: 105"
                            value={novoId}
                            onChange={e => setNovoId(e.target.value)}
                            autoFocus
                        />
                        <small className="helper-text">Insira o ID do usuário criado no sistema para vincular.</small>
                    </div>
                    <div className="modal-footer">
                        <button type="button" onClick={onClose} className="btn-ghost">Cancelar</button>
                        <button type="submit" className="btn-primary" disabled={loading}>
                            {loading ? 'Processando...' : 'Concluir e Admitir'} <FiArrowRight />
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const ModalVaga = ({ onClose, isEdit, vaga, areas, onSuccess }) => {
    const [form, setForm] = useState({
        titulo: '', area: '', cargo_alvo: '', tipo: 'nova_posicao', nivel: 'junior',
        salario_faixa: '', descricao: '', urgencia: 'normal', 
        motivo_substituicao: '', user_substituido_id: '', rescisao_id: ''
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (vaga) setForm({ ...form, ...vaga });
    }, [vaga]);

    const isSubstituicao = form.tipo === 'substituicao' || form.rescisao_id;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.titulo || !form.area) return toast.warn("Preencha os campos obrigatórios.");
        
        setLoading(true);
        try {
            if (isEdit) await api.put(`/api/rh/vagas/${vaga.id}`, form);
            else await api.post('/api/rh/vagas', form);
            toast.success(isEdit ? "Vaga atualizada!" : "Vaga criada com sucesso!");
            onSuccess();
            onClose();
        } catch (e) {
            toast.error("Erro ao salvar vaga.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="overlay-backdrop">
            <div className="modal-premium modal-lg">
                <div className="modal-header">
                    <h3>{isEdit ? <FiEdit2 /> : <FiPlus />} {isEdit ? 'Editar Vaga' : 'Nova Vaga'}</h3>
                    <button onClick={onClose} className="btn-close"><FiX /></button>
                </div>
                <form onSubmit={handleSubmit} className="modal-body grid-form">
                    {isSubstituicao && (
                        <div className="alert-box warning full-col">
                            <FiAlertTriangle />
                            <div>
                                <strong>Vaga de Substituição</strong>
                                <p>Vinculada a um processo de rescisão. Alguns dados são imutáveis.</p>
                            </div>
                        </div>
                    )}

                    <div className="form-group full-col">
                        <label className="input-label">Título da Vaga</label>
                        <input type="text" className="input-modern" value={form.titulo} onChange={e => setForm({...form, titulo: e.target.value})} required disabled={isSubstituicao} />
                    </div>

                    <div className="form-group">
                        <label className="input-label">Área / Departamento</label>
                        <select className="input-modern" value={form.area} onChange={e => setForm({...form, area: e.target.value})} required disabled={isSubstituicao}>
                            <option value="">Selecione...</option>
                            {areas.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="input-label">Cargo Alvo</label>
                        <input type="text" className="input-modern" value={form.cargo_alvo} onChange={e => setForm({...form, cargo_alvo: e.target.value})} required />
                    </div>

                    <div className="form-group">
                        <label className="input-label">Tipo de Vaga</label>
                        <select className="input-modern" value={form.tipo} onChange={e => setForm({...form, tipo: e.target.value})} disabled={isSubstituicao}>
                            <option value="nova_posicao">Nova Posição</option>
                            <option value="substituicao">Substituição</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="input-label">Nível Senioridade</label>
                        <select className="input-modern" value={form.nivel} onChange={e => setForm({...form, nivel: e.target.value})}>
                            <option value="estagio">Estágio</option>
                            <option value="junior">Júnior</option>
                            <option value="pleno">Pleno</option>
                            <option value="senior">Sênior</option>
                            <option value="especialista">Especialista</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="input-label">Faixa Salarial</label>
                        <input type="text" className="input-modern" placeholder="Ex: R$ 3.500 - R$ 4.500" value={form.salario_faixa} onChange={e => setForm({...form, salario_faixa: e.target.value})} />
                    </div>

                    <div className="form-group">
                        <label className="input-label">Urgência</label>
                        <select className="input-modern" value={form.urgencia} onChange={e => setForm({...form, urgencia: e.target.value})}>
                            <option value="baixa">Baixa</option>
                            <option value="normal">Normal</option>
                            <option value="alta">Alta</option>
                            <option value="critica">Crítica</option>
                        </select>
                    </div>

                    <div className="form-group full-col">
                        <label className="input-label">Descrição / Requisitos</label>
                        <textarea className="input-modern" rows="4" value={form.descricao} onChange={e => setForm({...form, descricao: e.target.value})}></textarea>
                    </div>

                    <div className="modal-footer full-col">
                        <button type="button" onClick={onClose} className="btn-ghost">Cancelar</button>
                        <button type="submit" className="btn-primary" disabled={loading}>
                            {loading ? 'Salvando...' : (isEdit ? 'Salvar Alterações' : 'Abrir Vaga')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Componente Principal ---
const GestaoVagas = () => {
    const navigate = useNavigate();
    const [vagas, setVagas] = useState([]);
    const [areas, setAreas] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const [viewMode, setViewMode] = useState('list'); // 'list' ou 'board'
    const [filterStatus, setFilterStatus] = useState('ativas');
    const [searchTerm, setSearchTerm] = useState('');

    const [modalState, setModalState] = useState({ new: false, edit: false, finish: false });
    const [selectedVaga, setSelectedVaga] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [vRes, aRes] = await Promise.all([
                api.get('/api/rh/vagas'),
                api.get('/api/rh/organograma/areas')
            ]);
            setVagas(vRes.data || []);
            setAreas(aRes.data || []);
        } catch (err) {
            toast.error("Erro ao carregar vagas.");
        } finally {
            // Simula um delay pequeno para o skeleton aparecer (UX)
            setTimeout(() => setLoading(false), 500);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Lógica de Filtragem
    const filteredList = useMemo(() => {
        let list = vagas;

        if (filterStatus === 'ativas') list = list.filter(v => ['aberta', 'em_andamento'].includes(v.status));
        else if (filterStatus === 'arquivadas') list = list.filter(v => ['preenchida', 'cancelada'].includes(v.status));
        else if (filterStatus === 'aberta') list = list.filter(v => v.status === 'aberta');
        else if (filterStatus === 'andamento') list = list.filter(v => v.status === 'em_andamento');

        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            list = list.filter(v => 
                v.titulo?.toLowerCase().includes(lower) || 
                v.area?.toLowerCase().includes(lower) ||
                v.id.toString().includes(lower)
            );
        }

        return list.sort((a, b) => new Date(b.data_abertura) - new Date(a.data_abertura));
    }, [vagas, filterStatus, searchTerm]);

    const stats = useMemo(() => {
        const counts = { aberta: 0, andamento: 0, preenchida: 0, ativas: 0 };
        vagas.forEach(v => {
            if (v.status === 'aberta') { counts.aberta++; counts.ativas++; }
            if (v.status === 'em_andamento') { counts.andamento++; counts.ativas++; }
            if (v.status === 'preenchida') counts.preenchida++;
        });
        return counts;
    }, [vagas]);

    const handleUpdateStatus = async (id, status) => {
        if (!window.confirm(`Alterar status para ${status.toUpperCase().replace('_', ' ')}?`)) return;
        try {
            await api.put(`/api/rh/vagas/${id}/status`, { status });
            toast.success("Status atualizado.");
            fetchData();
        } catch (e) { toast.error("Erro ao atualizar."); }
    };

    const handleConclude = async (vagaId, novoColaboradorId) => {
        try {
            await api.put(`/api/rh/vagas/${vagaId}/concluir`, { novo_colaborador_id: novoColaboradorId });
            toast.success("Vaga concluída! Redirecionando...");
            navigate(`/rh/base-funcionarios/${novoColaboradorId}`);
            setModalState({ ...modalState, finish: false });
        } catch (e) {
            toast.error("Erro ao concluir vaga.");
        }
    };

    return (
        <div className="vagas-layout fade-in">
            {/* Header */}
            <div className="page-header-row">
                <div className="header-title">
                    <h1><FiBriefcase /> Gestão de Vagas</h1>
                    <p>Recrutamento e Seleção</p>
                </div>
                <button className="btn-primary-lg" onClick={() => { setSelectedVaga(null); setModalState({ ...modalState, new: true }); }}>
                    <FiPlus /> Abrir Nova Vaga
                </button>
            </div>

            {/* KPI Cards */}
            <div className="kpi-container">
                <div className={`kpi-card ${filterStatus === 'ativas' ? 'selected' : ''}`} onClick={() => setFilterStatus('ativas')}>
                    <div className="kpi-icon bg-purple"><FiTrendingUp /></div>
                    <div><h3>{stats.ativas}</h3><p>Vagas Ativas</p></div>
                </div>
                <div className={`kpi-card ${filterStatus === 'aberta' ? 'selected' : ''}`} onClick={() => setFilterStatus('aberta')}>
                    <div className="kpi-icon bg-blue"><FiClock /></div>
                    <div><h3>{stats.aberta}</h3><p>Em Aberto</p></div>
                </div>
                <div className={`kpi-card ${filterStatus === 'andamento' ? 'selected' : ''}`} onClick={() => setFilterStatus('andamento')}>
                    <div className="kpi-icon bg-yellow"><FiSearch /></div>
                    <div><h3>{stats.andamento}</h3><p>Em Seleção</p></div>
                </div>
                <div className={`kpi-card ${filterStatus === 'arquivadas' ? 'selected' : ''}`} onClick={() => setFilterStatus('arquivadas')}>
                    <div className="kpi-icon bg-gray"><FiArchive /></div>
                    <div><p>Histórico</p></div>
                </div>
            </div>

            {/* Toolbar */}
            <div className="toolbar">
                <div className="search-box">
                    <FiSearch />
                    <input 
                        type="text" 
                        placeholder="Buscar vaga, área ou ID..." 
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="view-toggle">
                    <button 
                        className={viewMode === 'list' ? 'active' : ''} 
                        onClick={() => setViewMode('list')} 
                        title="Visualização em Lista"
                    >
                        <FiList /> Lista
                    </button>
                    <button 
                        className={viewMode === 'board' ? 'active' : ''} 
                        onClick={() => setViewMode('board')} 
                        title="Visualização em Quadro"
                    >
                        <FiGrid /> Quadro
                    </button>
                </div>
            </div>

            {/* Conteúdo (Lista ou Board) */}
            {loading ? (
                <div className="skeleton-container">
                    <SkeletonRow /><SkeletonRow /><SkeletonRow />
                </div>
            ) : (
                <>
                    {viewMode === 'list' ? (
                        <div className="table-card">
                            <table className="table-vagas">
                                <thead>
                                    <tr>
                                        <th width="25%">Vaga</th>
                                        <th>Área</th>
                                        <th>Tipo</th>
                                        <th>Prioridade</th>
                                        <th>Abertura</th>
                                        <th>Status</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredList.length === 0 ? (
                                        <tr><td colSpan="7" className="empty-cell">Nenhuma vaga encontrada.</td></tr>
                                    ) : (
                                        filteredList.map(vaga => (
                                            <tr key={vaga.id}>
                                                <td>
                                                    <div className="vaga-info-cell">
                                                        <div className="vaga-icon"><FaUserTie /></div>
                                                        <div>
                                                            <strong>{vaga.titulo || vaga.cargo_alvo}</strong>
                                                            <span>ID #{vaga.id} • {vaga.nivel}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td><span className="badge-area">{vaga.area}</span></td>
                                                <td>
                                                    {vaga.tipo === 'substituicao' 
                                                        ? <span className="badge-subst"><FiUserCheck /> Subst.</span> 
                                                        : <span className="badge-new"><FiPlus /> Nova</span>
                                                    }
                                                </td>
                                                <td><UrgencyBadge level={vaga.urgencia} /></td>
                                                <td>{new Date(vaga.data_abertura).toLocaleDateString()}</td>
                                                <td><StatusBadge status={vaga.status} /></td>
                                                <td className="text-right">
                                                    <div className="actions-wrapper">
                                                        <button className="btn-icon" onClick={() => { setSelectedVaga(vaga); setModalState({...modalState, edit: true}); }} title="Editar"><FiEdit2 /></button>
                                                        {vaga.status === 'aberta' && (
                                                            <button className="btn-icon success" onClick={() => handleUpdateStatus(vaga.id, 'em_andamento')} title="Iniciar Seleção"><FiArrowRight /></button>
                                                        )}
                                                        {vaga.status === 'em_andamento' && (
                                                            <button className="btn-icon primary" onClick={() => { setSelectedVaga(vaga); setModalState({...modalState, finish: true}); }} title="Concluir"><FiCheck /></button>
                                                        )}
                                                        {vaga.status !== 'preenchida' && vaga.status !== 'cancelada' && (
                                                            <button className="btn-icon danger" onClick={() => handleUpdateStatus(vaga.id, 'cancelada')} title="Cancelar"><FiTrash2 /></button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="kanban-board">
                            {['aberta', 'em_andamento', 'preenchida'].map(status => (
                                <div key={status} className="kanban-column">
                                    <div className={`column-header ${status}`}>
                                        <span>{status.replace('_', ' ').toUpperCase()}</span>
                                        <span className="count">{filteredList.filter(v => v.status === status).length}</span>
                                    </div>
                                    <div className="column-body">
                                        {filteredList.filter(v => v.status === status).map(vaga => (
                                            <div key={vaga.id} className="kanban-card" onClick={() => { setSelectedVaga(vaga); setModalState({...modalState, edit: true}); }}>
                                                <div className="card-top">
                                                    <span className="card-id">#{vaga.id}</span>
                                                    <FiMoreHorizontal />
                                                </div>
                                                <h4>{vaga.titulo}</h4>
                                                <p>{vaga.area}</p>
                                                <div className="card-tags">
                                                    <UrgencyBadge level={vaga.urgencia} />
                                                    {vaga.tipo === 'substituicao' && <span className="mini-tag subst">Subst.</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* Modais */}
            {(modalState.new || modalState.edit) && (
                <ModalVaga 
                    isOpen={true}
                    isEdit={modalState.edit}
                    vaga={selectedVaga}
                    areas={areas}
                    onClose={() => { setModalState({...modalState, new: false, edit: false}); setSelectedVaga(null); }}
                    onSuccess={fetchData}
                />
            )}

            {modalState.finish && selectedVaga && (
                <ModalConclusao 
                    vaga={selectedVaga}
                    onClose={() => { setModalState({...modalState, finish: false}); setSelectedVaga(null); }}
                    onConcluir={handleConclude}
                />
            )}
        </div>
    );
};

export default GestaoVagas;